import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAuth } from '@clerk/clerk-react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import type { Doc, Id } from 'convex/_generated/dataModel'

dayjs.extend(isoWeek)

export const Route = createFileRoute('/preview/schedule/calendar')({
  component: PreviewCalendarView,
})

function PreviewCalendarView() {
  const { isSignedIn } = useAuth()
  const currentUser = useCurrentUser()

  // Show loading state while checking auth status
  if (isSignedIn && currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  // If signed in, use real user data
  if (isSignedIn && currentUser) {
    return <CalendarViewContent userId={currentUser._id} isPreview={false} />
  }

  // If not signed in, use preview user data
  return <CalendarViewContent userId={null} isPreview={true} />
}

function CalendarViewContent({
  userId: _userId,
  isPreview
}: {
  userId: Id<'users'> | null
  isPreview: boolean
}) {
  const [currentDate, setCurrentDate] = useState(dayjs())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedReviews, setSelectedReviews] = useState<Doc<'vocabularyItems'>[]>([])
  const [selectedFlashcards, setSelectedFlashcards] = useState<Doc<'flashcards'>[]>([])

  // Calculate date range for current month
  const startOfMonth = currentDate.startOf('month')
  const endOfMonth = currentDate.endOf('month')

  // Query upcoming reviews for the month
  const upcomingReviews = useConvexQuery(
    api.preview.getPreviewUpcomingReviews,
    { startDate: startOfMonth.valueOf(), endDate: endOfMonth.valueOf() }
  )

  // Query study actions for the month
  const studyActions = useConvexQuery(
    api.preview.getPreviewStudyActions,
    { startDate: startOfMonth.valueOf(), endDate: endOfMonth.valueOf() }
  )

  // Query upcoming flashcards for the month
  const upcomingFlashcards = useConvexQuery(
    api.preview.getPreviewUpcomingFlashcards,
    { startDate: startOfMonth.valueOf(), endDate: endOfMonth.valueOf() }
  )

  // Debug: Log query results
  if (isPreview && upcomingFlashcards !== undefined) {
    console.log('[Preview Calendar] Upcoming flashcards query result:', {
      count: upcomingFlashcards.length,
      dateRange: {
        start: dayjs(startOfMonth.valueOf()).format('YYYY-MM-DD'),
        end: dayjs(endOfMonth.valueOf()).format('YYYY-MM-DD')
      },
      sampleFlashcards: upcomingFlashcards.slice(0, 3).map(f => ({
        id: f._id,
        dueDate: dayjs(f.dueDate).format('YYYY-MM-DD'),
        interval: f.interval,
        repetitions: f.repetitions
      }))
    })
  }

  // Group reviews by date
  const reviewsByDate = useMemo(() => {
    if (!upcomingReviews) return {}

    const grouped: Record<string, Doc<'vocabularyItems'>[]> = {}

    for (const review of upcomingReviews) {
      const dateKey = dayjs(review.dueDate).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(review)
    }

    return grouped
  }, [upcomingReviews])

  // Group study actions by date
  const actionsByDate = useMemo(() => {
    if (!studyActions) return {}

    const grouped: Record<string, Doc<'studyActions'>[]> = {}

    for (const action of studyActions) {
      if (!action.scheduledDate) continue
      const dateKey = dayjs(action.scheduledDate).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(action)
    }

    return grouped
  }, [studyActions])

  // Group flashcards by date
  const flashcardsByDate = useMemo(() => {
    if (!upcomingFlashcards) return {}

    const grouped: Record<string, Doc<'flashcards'>[]> = {}

    for (const flashcard of upcomingFlashcards) {
      const dateKey = dayjs(flashcard.dueDate).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(flashcard)
    }

    // Debug: Log flashcards distribution
    if (isPreview && Object.keys(grouped).length > 0) {
      console.log('[Preview Calendar] Flashcards by date:', Object.keys(grouped).map(key => ({
        date: key,
        count: grouped[key].length
      })))
      console.log('[Preview Calendar] Total flashcards:', upcomingFlashcards.length)
      console.log('[Preview Calendar] Date range:', {
        start: dayjs(startOfMonth.valueOf()).format('YYYY-MM-DD'),
        end: dayjs(endOfMonth.valueOf()).format('YYYY-MM-DD')
      })
    }

    return grouped
  }, [upcomingFlashcards, isPreview, startOfMonth, endOfMonth])

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'))
    setSelectedDate(null)
    setSelectedReviews([])
    setSelectedFlashcards([])
  }

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'))
    setSelectedDate(null)
    setSelectedReviews([])
    setSelectedFlashcards([])
  }

  const handleToday = () => {
    setCurrentDate(dayjs())
    setSelectedDate(null)
    setSelectedReviews([])
    setSelectedFlashcards([])
  }

  const handleDateClick = (dateKey: string) => {
    const reviews = reviewsByDate[dateKey] || []
    const flashcards = flashcardsByDate[dateKey] || []
    console.log('[Preview Calendar] Date clicked:', dateKey, {
      reviews: reviews.length,
      flashcards: flashcards.length,
      flashcardIds: flashcards.map(f => f._id)
    })
    setSelectedDate(dateKey)
    setSelectedReviews(reviews)
    setSelectedFlashcards(flashcards)
  }

  const handleCloseSidebar = () => {
    setSelectedDate(null)
    setSelectedReviews([])
    setSelectedFlashcards([])
  }

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Preview Banner */}
        {isPreview && (
          <div className="mb-4 bg-terminal-green/20 border-2 border-terminal-green p-3 text-center">
            <p className="text-terminal-green font-mono text-sm font-bold">
              [PREVIEW MODE] • Sign up to save your calendar and review sessions
            </p>
          </div>
        )}

        {/* Header */}
        <div className="mb-8 terminal-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
              [REVIEW CALENDAR]
            </h1>
            <Link
              to={isPreview ? "/preview/schedule" : "/app/schedule"}
              className="px-4 py-2 bg-black hover:bg-gray-900 text-trail-tan font-bold font-mono border-2 border-trail-tan rounded-lg transition-colors"
            >
              BACK TO DASHBOARD
            </Link>
          </div>
          <p className="text-trail-tan font-mono">
            View your scheduled vocabulary reviews
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Calendar Navigation */}
          <div className={`flex-1 terminal-border bg-black p-4 sm:p-6 ${selectedDate ? 'lg:w-2/3' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handlePrevMonth}
                className="px-4 py-2 bg-black hover:bg-gray-900 text-trail-tan font-bold font-mono border-2 border-trail-tan rounded-lg transition-colors"
              >
                ← PREVIOUS
              </button>

              <div className="text-center">
                <h2 className="text-2xl font-bold text-sky-blue font-mono">
                  {currentDate.format('MMMM YYYY')}
                </h2>
                <button
                  onClick={handleToday}
                  className="text-sm text-terminal-green hover:underline mt-1 font-mono"
                >
                  Today
                </button>
              </div>

              <button
                onClick={handleNextMonth}
                className="px-4 py-2 bg-black hover:bg-gray-900 text-trail-tan font-bold font-mono border-2 border-trail-tan rounded-lg transition-colors"
              >
                NEXT →
              </button>
            </div>

            {/* Calendar Grid */}
            <CalendarGrid
              currentDate={currentDate}
              reviewsByDate={reviewsByDate}
              actionsByDate={actionsByDate}
              flashcardsByDate={flashcardsByDate}
              onDateClick={handleDateClick}
              selectedDate={selectedDate}
            />
          </div>

          {/* Right Sidebar - Review Preview */}
          {/* Mobile: Render below calendar, Desktop: Render on the right */}
          {selectedDate && (
            <div className={`w-full ${selectedDate ? 'lg:w-1/3' : ''}`}>
              <ReviewSidebar
                date={selectedDate}
                reviews={selectedReviews}
                flashcards={selectedFlashcards}
                onClose={handleCloseSidebar}
                isPreview={isPreview}
              />
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 terminal-border bg-black p-6">
          <h3 className="text-lg font-semibold text-sky-blue mb-4 font-mono">LEGEND</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-900/30 border-2 border-blue-500 rounded" />
              <span className="text-sm text-trail-tan font-mono">Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-900/30 rounded" />
              <span className="text-sm text-trail-tan font-mono">1-5 items</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-yellow-900/30 rounded" />
              <span className="text-sm text-trail-tan font-mono">6-10 items</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-900/30 rounded" />
              <span className="text-sm text-trail-tan font-mono">11+ items</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CalendarGrid({
  currentDate,
  reviewsByDate,
  actionsByDate,
  flashcardsByDate,
  onDateClick,
  selectedDate,
}: {
  currentDate: dayjs.Dayjs
  reviewsByDate: Record<string, Doc<'vocabularyItems'>[]>
  actionsByDate: Record<string, Doc<'studyActions'>[]>
  flashcardsByDate: Record<string, Doc<'flashcards'>[]>
  onDateClick: (dateKey: string) => void
  selectedDate: string | null
}) {
  const today = dayjs().format('YYYY-MM-DD')
  const startOfMonth = currentDate.startOf('month')
  const endOfMonth = currentDate.endOf('month')

  // Calculate calendar grid (6 weeks to show full month)
  const startDay = startOfMonth.day() // 0-6 (Sunday-Saturday)
  const daysInMonth = endOfMonth.date()

  // Generate all days in calendar grid
  const calendarDays: Array<{ date: dayjs.Dayjs; isCurrentMonth: boolean }> = []

  // Previous month days
  const prevMonthEnd = startOfMonth.subtract(1, 'day')
  for (let i = startDay - 1; i >= 0; i--) {
    calendarDays.push({
      date: prevMonthEnd.subtract(i, 'day'),
      isCurrentMonth: false,
    })
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      date: startOfMonth.date(i),
      isCurrentMonth: true,
    })
  }

  // Next month days to fill grid (up to 42 days total = 6 weeks)
  const remainingDays = 42 - calendarDays.length
  for (let i = 1; i <= remainingDays; i++) {
    calendarDays.push({
      date: endOfMonth.add(i, 'day'),
      isCurrentMonth: false,
    })
  }

  return (
    <div>
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-sm font-semibold text-sky-blue font-mono py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, index) => {
          const dateKey = day.date.format('YYYY-MM-DD')
          const reviews = reviewsByDate[dateKey] || []
          const actions = actionsByDate[dateKey] || []
          const flashcards = flashcardsByDate[dateKey] || []
          const reviewCount = reviews.length
          const actionCount = actions.length
          const flashcardCount = flashcards.length
          const totalItems = reviewCount + actionCount + flashcardCount
          const isToday = dateKey === today
          const isSelected = selectedDate === dateKey

          return (
            <CalendarDay
              key={index}
              date={day.date}
              isCurrentMonth={day.isCurrentMonth}
              isToday={isToday}
              isSelected={isSelected}
              reviewCount={reviewCount}
              actionCount={actionCount}
              flashcardCount={flashcardCount}
              totalItems={totalItems}
              reviews={reviews}
              actions={actions}
              flashcards={flashcards}
              onClick={() => onDateClick(dateKey)}
            />
          )
        })}
      </div>
    </div>
  )
}

function CalendarDay({
  date,
  isCurrentMonth,
  isToday,
  isSelected,
  reviewCount,
  actionCount,
  flashcardCount,
  totalItems,
  reviews: _reviews,
  actions: _actions,
  flashcards: _flashcards,
  onClick,
}: {
  date: dayjs.Dayjs
  isCurrentMonth: boolean
  isToday: boolean
  isSelected: boolean
  reviewCount: number
  actionCount: number
  flashcardCount: number
  totalItems: number
  reviews: Doc<'vocabularyItems'>[]
  actions: Doc<'studyActions'>[]
  flashcards: Doc<'flashcards'>[]
  onClick: () => void
}) {
  // Determine background color based on total items
  const getBackgroundColor = () => {
    if (totalItems === 0) return 'bg-black'
    if (totalItems <= 5) return 'bg-green-900/30'
    if (totalItems <= 10) return 'bg-yellow-900/30'
    return 'bg-red-900/30'
  }

  // Days with flashcards should have blue border
  const hasFlashcards = flashcardCount > 0
  const hasAnyItems = totalItems > 0

  return (
    <div
      className={`
        aspect-square rounded-lg border-2 transition-all
        ${getBackgroundColor()}
        ${hasFlashcards ? 'border-sky-blue' : hasAnyItems ? 'border-terminal-green/50' : 'border-sky-blue/30'}
        ${isToday ? 'border-blue-500 ring-2 ring-blue-500/50' : ''}
        ${isSelected ? 'ring-2 ring-terminal-green ring-offset-2 ring-offset-black' : ''}
        ${!isCurrentMonth ? 'opacity-40' : ''}
        ${hasAnyItems ? 'cursor-pointer hover:border-terminal-green hover:shadow-lg hover:scale-105' : 'cursor-default'}
      `}
      onClick={hasAnyItems ? onClick : undefined}
    >
      <div className="p-1 sm:p-2 h-full flex flex-col">
        <div className={`text-xs sm:text-sm font-medium font-mono ${!isCurrentMonth ? 'text-gray-500' : 'text-white'}`}>
          {date.date()}
        </div>
        {totalItems > 0 && (
          <div className="flex-1 flex flex-col items-center justify-center gap-0.5 mt-1">
            {reviewCount > 0 && (
              <span className="text-[10px] sm:text-xs font-semibold text-trail-tan font-mono">
                {reviewCount} {reviewCount === 1 ? 'vocab' : 'vocab'}
              </span>
            )}
            {flashcardCount > 0 && (
              <span className="text-[10px] sm:text-xs font-semibold text-terminal-green font-mono">
                {flashcardCount} {flashcardCount === 1 ? 'card' : 'cards'}
              </span>
            )}
            {actionCount > 0 && (
              <span className="text-[10px] sm:text-xs font-semibold text-sky-blue font-mono">
                {actionCount} {actionCount === 1 ? 'action' : 'actions'}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ReviewSidebar({
  date,
  reviews,
  flashcards,
  onClose,
  isPreview,
}: {
  date: string
  reviews: Doc<'vocabularyItems'>[]
  flashcards: Doc<'flashcards'>[]
  onClose: () => void
  isPreview: boolean
}) {
  const reviewUrl = isPreview ? '/preview/schedule' : '/app/schedule/review'
  const flashcardUrl = isPreview ? '/preview/flashcards' : '/app/flashcards'

  return (
    <div className="w-full terminal-border bg-black p-4 sm:p-6 lg:sticky lg:top-8 max-h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-4rem)] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-sky-blue font-mono">
          {dayjs(date).format('MMMM D, YYYY')}
        </h2>
        <button
          onClick={onClose}
          className="text-trail-tan hover:text-terminal-green font-mono text-lg"
          aria-label="Close sidebar"
        >
          ×
        </button>
      </div>

      <div className="mb-4">
        <p className="text-trail-tan font-mono text-sm mb-2">
          {reviews.length > 0 && (
            <>{reviews.length} {reviews.length === 1 ? 'vocabulary item' : 'vocabulary items'} due for review</>
          )}
          {reviews.length > 0 && flashcards.length > 0 && <><br /></>}
          {flashcards.length > 0 && (
            <>{flashcards.length} {flashcards.length === 1 ? 'flashcard' : 'flashcards'} due for review</>
          )}
        </p>
      </div>

      {/* Vocabulary Review Cards Preview */}
      {reviews.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sky-blue font-mono font-bold text-sm mb-2">VOCABULARY ITEMS</h3>
          <div className="space-y-3">
            {reviews.map((review) => (
              <div
                key={review._id}
                className="terminal-border p-4 bg-black border-sky-blue/30"
              >
                <div className="font-semibold text-white font-mono mb-1">
                  {review.word}
                </div>
                {review.translation && (
                  <div className="text-sm text-trail-tan font-mono mb-2">
                    {review.translation}
                  </div>
                )}
                {review.context && (
                  <div className="text-xs text-gray-400 font-mono italic">
                    &quot;{review.context}&quot;
                  </div>
                )}
                {review.partOfSpeech && (
                  <div className="text-xs text-sky-blue font-mono mt-1">
                    ({review.partOfSpeech})
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flashcard Preview */}
      {flashcards.length > 0 && (
        <div className="mb-6">
          <h3 className="text-terminal-green font-mono font-bold text-sm mb-2">FLASHCARDS</h3>
          <div className="space-y-3">
            {flashcards.map((flashcard) => (
              <div
                key={flashcard._id}
                className="terminal-border p-4 bg-black border-terminal-green/30"
              >
                <div className="font-semibold text-white font-mono mb-1">
                  {flashcard.frontSpanish.join(' / ')}
                </div>
                <div className="text-sm text-trail-tan font-mono mb-2">
                  {flashcard.backEnglish.join(' / ')}
                </div>
                {flashcard.targetVerb && (
                  <div className="text-xs text-terminal-green font-mono mt-1">
                    Verb: {flashcard.targetVerb}
                  </div>
                )}
                <div className="text-xs text-gray-400 font-mono mt-1">
                  Interval: {flashcard.interval} day{flashcard.interval !== 1 ? 's' : ''} • Repetitions: {flashcard.repetitions}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Early Review Buttons */}
      <div className="space-y-3">
        {reviews.length > 0 && (
          <Link
            to={reviewUrl}
            className="block w-full px-6 py-3 bg-terminal-green hover:bg-terminal-green/80 text-black font-bold font-mono text-center border-2 border-terminal-green rounded-lg transition-colors"
          >
            START VOCAB REVIEW ({reviews.length} {reviews.length === 1 ? 'item' : 'items'})
          </Link>
        )}
        {flashcards.length > 0 && (
          <Link
            to={flashcardUrl}
            className="block w-full px-6 py-3 bg-sky-blue hover:bg-sky-blue/80 text-black font-bold font-mono text-center border-2 border-sky-blue rounded-lg transition-colors"
          >
            START FLASHCARD REVIEW ({flashcards.length} {flashcards.length === 1 ? 'card' : 'cards'})
          </Link>
        )}
      </div>
    </div>
  )
}

